interface RequestSettings {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers: Record<string, string>;
  body?: string;
  credentials?: 'include' | 'same-origin' | 'omit';
}

interface FetchOptions<TResponse, TBody> {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  resource: string;
  body?: TBody;
  token?: string;
  adapter?: (data: any) => TResponse;
  errors?: Record<number, string>;
  skipAuthErrorHandling?: boolean; // Опция для отключения автоматической обработки ошибок авторизации
}

class FetchError extends Error {
  constructor(
    public status: number,
    public data: any,
    message: string
  ) {
    super(message);
  }
}

const defaultErrors: Record<number, string> = {
  500: 'Ошибка сервера',
  401: 'Не авторизован',
  403: 'Доступ запрещён',
  404: 'Ресурс не найден',
  422: 'Ошибка данных',
  408: 'Время ожидания истекло',
};

export async function useFetch<TResponse, TBody>(
  options: FetchOptions<TResponse, TBody>
): Promise<{ status: number; data: TResponse }> {
  const {
    method,
    resource,
    body,
    token,
    adapter,
    errors = {},
    skipAuthErrorHandling = false,
  } = options;

  const url = new URL(resource, import.meta.env.VITE_APP_BASEURL);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const request: RequestSettings = {
    method,
    headers,
    // Убираем credentials поскольку теперь используем Bearer токены
  };

  if ((method === 'POST' || method === 'PUT' || method === 'DELETE') && body) {
    request.body = JSON.stringify(body);
  }

  if (method === 'GET' && body && typeof body === 'object') {
    Object.entries(body as Record<string, any>).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  try {
    const response = await fetch(url.toString(), request);
    const status = response.status;
    const data = await response.json();

    if (response.ok) {
      return {
        status,
        data: adapter ? adapter(data) : data,
      };
    } else {
      const error = new FetchError(
        status,
        data,
        errors[status] || defaultErrors[status] || 'Неизвестная ошибка'
      );

      // Логируем API ошибки в Sentry (только если Sentry включен)
      if (typeof window !== 'undefined') {
        const { SentryService } = await import('@/shared/lib/sentry/sentryService');
        SentryService.captureException(error, {
          api_url: url.toString(),
          method,
          status,
          response_data: data,
          request_body: body,
        });
      }

      // Автоматическая обработка ошибок авторизации
      if (!skipAuthErrorHandling && status === 401) {
        try {
          // Импортируем динамически чтобы избежать циклических зависимостей
          const { handleAuthError } = await import('@/shared/lib/auth/authErrorHandler');
          const handled = await handleAuthError(status, data);
          
          // Если токен был успешно обновлен, можно повторить запрос
          // Но пока просто выбрасываем ошибку для обработки на уровне компонента
        } catch (handlerError) {
          console.error('Ошибка в обработчике авторизации:', handlerError);
        }
      }

      throw error;
    }
  } catch (error) {
    // Логируем сетевые ошибки в Sentry
    if (error instanceof FetchError) {
      throw error; // Уже обработано выше
    }
    
    if (typeof window !== 'undefined') {
      const { SentryService } = await import('@/shared/lib/sentry/sentryService');
      SentryService.captureException(error as Error, {
        api_url: url.toString(),
        method,
        request_body: body,
        error_type: 'network_error',
      });
    }
    
    throw error;
  }
}
